
import { useState } from "react";
import { Share2, Copy, Check, Linkedin, Twitter, Facebook, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import { analytics } from "@/services/analytics";
import { useMediaQuery } from "@/hooks/useMediaQuery";

interface SharePromptButtonProps {
  promptId: string;
  title: string;
  description?: string;
  variant?: "default" | "outline" | "secondary" | "ghost" | "link" | "destructive";
  size?: "default" | "sm" | "lg";
  className?: string;
}

const SharePromptButton = ({ 
  promptId, 
  title, 
  description = "", 
  variant = "outline",
  size = "default",
  className = ""
}: SharePromptButtonProps) => {
  const [isCopied, setIsCopied] = useState(false);
  const isMobile = useMediaQuery("(max-width: 640px)");
  
  const siteUrl = window.location.origin;
  const shareUrl = `${siteUrl}/prompt/${promptId}`;
  
  // Function to generate sharing URLs for different platforms
  const getSharingUrl = (platform: string) => {
    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedTitle = encodeURIComponent(title);
    const encodedDescription = encodeURIComponent(description || "");
    
    switch (platform) {
      case 'linkedin':
        return `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
      case 'twitter':
        return `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`;
      case 'facebook':
        return `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
      case 'reddit':
        return `https://www.reddit.com/submit?url=${encodedUrl}&title=${encodedTitle}`;
      default:
        return shareUrl;
    }
  };
  
  // Copy URL to clipboard
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setIsCopied(true);
      toast.success("Link copied to clipboard");
      
      // Track share event
      analytics.trackPromptShare(promptId, "copy", title);
      
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    } catch (error) {
      console.error("Failed to copy:", error);
      toast.error("Failed to copy link to clipboard");
    }
  };
  
  // Handle sharing to a social platform
  const handleShare = (platform: string) => {
    const url = getSharingUrl(platform);
    window.open(url, "_blank", "noopener,noreferrer");
    
    // Track share event
    analytics.trackPromptShare(promptId, platform, title);
  };
  
  // Sharing options
  const shareOptions = [
    { name: "LinkedIn", icon: Linkedin, platform: "linkedin" },
    { name: "Twitter", icon: Twitter, platform: "twitter" },
    { name: "Facebook", icon: Facebook, platform: "facebook" },
    { name: "Copy Link", icon: isCopied ? Check : Copy, platform: "copy" }
  ];

  // If mobile, use Sheet component for better UX
  if (isMobile) {
    return (
      <Sheet>
        <SheetTrigger asChild>
          <Button 
            variant={variant} 
            size={size}
            className={className}
            data-stop-propagation="true"
          >
            <Share2 className="h-4 w-4 mr-2" />
            Share
          </Button>
        </SheetTrigger>
        <SheetContent side="bottom">
          <SheetHeader>
            <SheetTitle>Share Prompt</SheetTitle>
          </SheetHeader>
          <div className="grid grid-cols-3 gap-4 py-6">
            {shareOptions.map((option) => (
              <button
                key={option.platform}
                className="flex flex-col items-center justify-center gap-2 p-4 rounded-lg hover:bg-accent"
                onClick={() => {
                  option.platform === "copy" 
                    ? copyToClipboard() 
                    : handleShare(option.platform);
                }}
                data-stop-propagation="true"
              >
                <div className="h-10 w-10 flex items-center justify-center bg-primary/10 rounded-full">
                  <option.icon className="h-5 w-5" />
                </div>
                <span className="text-sm">{option.name}</span>
              </button>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    );
  }
  
  // For desktop, use dropdown menu
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant={variant} 
          size={size}
          className={className}
          data-stop-propagation="true"
        >
          <Share2 className="h-4 w-4 mr-2" />
          Share
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56">
        {shareOptions.map((option) => (
          <DropdownMenuItem
            key={option.platform}
            className="cursor-pointer flex items-center gap-2 py-2"
            onClick={() => {
              option.platform === "copy" 
                ? copyToClipboard() 
                : handleShare(option.platform);
            }}
            data-stop-propagation="true"
          >
            <option.icon className="h-4 w-4" />
            <span>{option.name}</span>
            {option.platform !== "copy" && (
              <ExternalLink className="h-3 w-3 ml-auto" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default SharePromptButton;
